import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Wrench, CalendarBlank, User } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AppHeader } from "@/components/AppHeader";
import { FilterBar } from "@/components/FilterBar";
import { api, formatApiErrorDetail } from "@/lib/api";

const FILTERS = ["All", "Open", "Scheduled", "On site", "Done"];
const BADGE = {
  Open: "bg-red-500/10 text-red-300 border-red-500/25",
  Scheduled: "bg-blue-500/10 text-blue-300 border-blue-500/25",
  "On site": "bg-yellow-500/10 text-yellow-300 border-yellow-500/25",
  Done: "bg-emerald-500/10 text-emerald-300 border-emerald-500/25",
};
const PRIORITY = {
  Urgent: "text-red-400",
  Normal: "text-zinc-400",
  Low: "text-zinc-500",
};

export default function ServiceCalls() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState([]);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    const [list, st] = await Promise.all([
      api.get("/service-calls", { params }),
      api.get("/service-calls/stats"),
    ]);
    setItems(list.data);
    setStats(st.data);
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const shown = items.filter(
    (c) => filter.length === 0 || filter.includes(c.status),
  );

  const cycle = async (call) => {
    const next =
      call.status === "Done"
        ? "Open"
        : call.status === "On site"
          ? "Done"
          : call.status === "Scheduled"
            ? "On site"
            : "Scheduled";
    try {
      await api.patch(`/service-calls/${call.id}/status`, { status: next });
      toast.success(`${call.code} · ${next}`);
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not update",
      );
    }
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Jobs / Service Calls" />
      <main className="relative z-10 mx-auto max-w-4xl px-4 pb-28 pt-6">
        <div
          className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
          data-testid="service-header-card"
        >
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                Jobs / Service
              </div>
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                Service Calls
              </h1>
              <p className="mt-1 text-sm text-zinc-500">
                Repairs and warranty visits — scheduled calls appear on the jobs
                schedule and day sheets
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/projects/schedule"
                data-testid="service-schedule-link"
                className="flex h-12 items-center rounded-md border border-zinc-700 px-4 text-xs font-semibold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
              >
                Schedule
              </Link>
              <Button
                data-testid="new-service-call-button"
                onClick={() => navigate("/projects/service/new")}
                className="h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400"
              >
                <Plus size={18} weight="bold" className="mr-2" /> New Service
                Call
              </Button>
            </div>
          </div>
        </div>

        {stats && (
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              ["Total", stats.total, "text-white"],
              ["Open", stats.Open, "text-red-400"],
              ["Scheduled", stats.Scheduled, "text-blue-400"],
              ["Done", stats.Done, "text-emerald-400"],
            ].map(([k, v, c]) => (
              <div
                key={k}
                data-testid={`service-stat-${k.toLowerCase()}`}
                className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
              >
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  {k}
                </div>
                <div className={`font-mono-data mt-1 text-2xl font-bold ${c}`}>
                  {v}
                </div>
              </div>
            ))}
          </div>
        )}

        <FilterBar
          className="mt-8"
          testidPrefix="service-calls"
          search={{
            value: q,
            onChange: setQ,
            placeholder: "Search customer, issue, address, code…",
            testid: "service-search-input",
          }}
          sections={[
            {
              key: "status",
              label: "Status",
              multi: true,
              values: filter,
              allValue: "All",
              onChange: setFilter,
              options: FILTERS.map((f) => [
                f,
                f,
                `service-filter-${f.toLowerCase().replace(/ /g, "-")}`,
              ]),
            },
          ]}
        />

        <div className="mt-6 space-y-3" data-testid="service-list">
          {shown.length === 0 && (
            <div
              data-testid="service-empty-state"
              className="rounded-lg border border-dashed border-zinc-800 p-10 text-center"
            >
              <Wrench
                size={28}
                weight="bold"
                className="mx-auto text-zinc-700"
              />
              <p className="mt-3 text-sm text-zinc-400">
                No service calls logged.
              </p>
            </div>
          )}
          {shown.map((c) => (
            <div
              key={c.id}
              data-testid={`service-card-${c.id}`}
              className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
            >
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-mono-data text-xs text-yellow-500">
                  {c.code}
                </span>
                <span
                  className={`rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wider ${
                    BADGE[c.status] || BADGE.Open
                  }`}
                >
                  {c.status}
                </span>
                <span
                  className={`text-xs uppercase tracking-widest ${PRIORITY[c.priority]}`}
                >
                  {c.priority}
                </span>
                {c.job_code && (
                  <Link
                    to={`/projects/${c.job_id}?tab=field`}
                    className="text-xs text-zinc-400 hover:text-yellow-400"
                  >
                    from {c.job_code}
                  </Link>
                )}
              </div>
              <div className="mt-2 text-lg font-bold">{c.issue_type}</div>
              <div className="text-sm text-zinc-400">
                {c.customer_name || "—"}
                {c.address ? ` · ${c.address}` : ""}
              </div>
              {c.description && (
                <p className="mt-2 text-sm text-zinc-400">{c.description}</p>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-zinc-400">
                <span className="font-mono-data inline-flex items-center gap-1">
                  <CalendarBlank size={13} />{" "}
                  {c.scheduled_date || "Not scheduled"}
                  {c.scheduled_date ? ` · ${c.window}` : ""}
                </span>
                <span className="inline-flex items-center gap-1">
                  <User size={13} /> {c.crew || "Unassigned"}
                </span>
              </div>
              <div className="mt-4 flex gap-2">
                <Button
                  data-testid={`service-advance-${c.id}`}
                  variant="outline"
                  onClick={() => cycle(c)}
                  className="h-11 border-zinc-700 bg-transparent text-xs font-semibold uppercase tracking-wider hover:bg-zinc-800"
                >
                  {c.status === "Done" ? "Reopen" : "Advance status"}
                </Button>
                <Button
                  data-testid={`service-edit-${c.id}`}
                  variant="outline"
                  onClick={() => navigate(`/projects/service/${c.id}/edit`)}
                  className="h-11 border-zinc-700 bg-transparent text-xs font-semibold uppercase tracking-wider hover:bg-zinc-800"
                >
                  Edit
                </Button>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
